import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  movimentoEsitoCampi,
  movimentoHeaderSubtitle,
  movimentoIdentificativo,
  movimentoIntegrazioneCampi,
  movimentoProvenienzaCampi,
  movimentoRiferimentiCampi,
  movimentoRifiutoCampi,
  movimentoRowFields,
  movimentoTabHasData,
  type MovimentoRecord,
  type MovimentoTabId,
} from "@/lib/movimento";
import { cn } from "@/lib/utils";

const TABS: { id: MovimentoTabId; label: string }[] = [
  { id: "riferimenti", label: "Riferimenti" },
  { id: "rifiuto", label: "Rifiuto" },
  { id: "integrazione", label: "Integrazione FIR / Registro C/S" },
  { id: "esito", label: "Esito conferimento" },
  { id: "provenienza", label: "Provenienza" },
  { id: "annotazioni", label: "Annotazioni" },
];

function formatCampoValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function CampiGrid({ campi }: { campi: { label: string; value: unknown }[] }) {
  return (
    <dl className="grid gap-4 sm:grid-cols-2">
      {campi.map(({ label, value }, idx) => (
        <div key={`${idx}-${label}`} className="space-y-1">
          <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
          <dd className="text-sm break-words whitespace-pre-wrap">{formatCampoValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

export function MovimentoDettaglioDialog({
  movimento,
  open,
  onOpenChange,
}: {
  movimento: MovimentoRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [tab, setTab] = useState<MovimentoTabId>("riferimenti");

  useEffect(() => {
    if (open) setTab("riferimenti");
  }, [open, movimento]);

  const m = movimento;
  const summary = m ? movimentoRowFields(m) : null;

  const campiTab = useMemo(() => {
    if (!m) return [];
    switch (tab) {
      case "riferimenti":
        return movimentoRiferimentiCampi(m);
      case "rifiuto":
        return movimentoRifiutoCampi(m);
      case "integrazione":
        return movimentoIntegrazioneCampi(m);
      case "esito":
        return movimentoEsitoCampi(m);
      case "provenienza":
        return movimentoProvenienzaCampi(m);
      case "annotazioni":
        return [{ label: "Annotazioni", value: m.annotazioni ?? "—" }];
      default:
        return [];
    }
  }, [m, tab]);

  if (!m) {
    return <Dialog open={false} onOpenChange={onOpenChange} />;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,720px)] max-w-3xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-3 border-b px-6 py-4 text-left">
          <DialogTitle className="text-base font-semibold">Dettaglio registrazione</DialogTitle>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="font-mono text-xs font-normal">
              {movimentoIdentificativo(m)}
            </Badge>
            <Badge variant="secondary" className="text-xs font-normal">
              {movimentoHeaderSubtitle(m)}
            </Badge>
          </div>
          {summary?.trasmesso && summary.trasmesso !== "—" && (
            <p className="text-xs text-muted-foreground">
              Data e ora trasmissione{" "}
              <span className="font-medium text-foreground">{summary.trasmesso}</span>
            </p>
          )}
        </DialogHeader>

        <div className="flex flex-wrap gap-1 border-b px-4 pt-2">
          {TABS.map((t) => {
            const enabled = movimentoTabHasData(m, t.id);
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                disabled={!enabled}
                onClick={() => enabled && setTab(t.id)}
                className={cn(
                  "rounded-t-md px-3 py-2 text-xs font-medium transition-colors",
                  active && enabled
                    ? "border-b-2 border-primary text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                  !enabled && "cursor-not-allowed opacity-40",
                )}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <CampiGrid campi={campiTab} />
        </div>

        <DialogFooter className="border-t px-6 py-3 sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
